import type { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Briefcase, Plus, TriangleAlert } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useNavigate } from "react-router-dom";

import { useAppHeader, type AppHeaderConfig } from "@/components/app-header";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Spinner } from "@/components/ui/spinner";

import { CaseStatus } from "../api/types";
import { MatterDTO } from "../api/types/matter";
import { VisaTag } from "../components/common/VisaTag";
import {
  caseWorkspacePath,
  matterCaseRef,
  NEW_CASE_PATH
} from "../features/caseIde/routes";
import {
  caseChipCounts,
  type CaseChipCounts
} from "../features/matters/casesChips";
import { AssignedUsersAvatarStack } from "../features/foreignNationals/components/AssignedUsersAvatarStack";
import { CaseStatusPill } from "../features/matters/components/CaseStatusPill";
import {
  CasesTableToolbar,
  initialsFromLabel
} from "../features/matters/components/CasesTableToolbar";
import {
  MattersDashboardProvider,
  useMattersDashboard
} from "../features/matters/context/MattersDashboardContext";
import { caseTypeLabel } from "../features/customCaseTypes/customCaseTypes";
import { useUserProfiles } from "../features/userProfiles/context/UserProfilesContext";
import { usePracticeTeamMembers } from "../hooks/usePracticeTeamMembers";
import { useUserRoleAndSubscription } from "../hooks/useUserRoleAndSubscription";
import { asVisaValue, getPetitionerName } from "../utils/matterDisplay";

const CASES_HEADER: AppHeaderConfig = {
  crumbs: [{ id: "cases", label: "Cases", current: true, icon: Briefcase }]
};

/** The page description: whose cases these are. */
export const casesDescription = ({
  isSuperAdmin,
  practiceName
}: {
  isSuperAdmin: boolean;
  practiceName?: string | null;
}): string => {
  if (isSuperAdmin) return "Every case across all firms.";
  const firm = (practiceName ?? "").trim();
  return firm ? `Every case for ${firm}.` : "Every case at your firm.";
};

const asString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  return String(value);
};

const getIndividualField = (ind: Record<string, unknown>, keys: string[]) => {
  for (const k of keys) {
    const v = ind[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

const getClientName = (matter: MatterDTO): string => {
  const ind = (matter.individual ?? {}) as Record<string, unknown>;
  const first = getIndividualField(ind, ["firstName", "first_name"]);
  const last = getIndividualField(ind, ["lastName", "last_name"]);
  const full = `${first} ${last}`.trim();
  return full || getIndividualField(ind, ["name", "fullName"]) || "Unknown";
};

const getForeignNationalId = (matter: MatterDTO): string => {
  const raw = matter.individual?.foreign_national_id;
  return typeof raw === "string" ? raw.trim() : "";
};

const getPracticeName = (matter: MatterDTO): string => {
  const ind = (matter.individual ?? {}) as Record<string, unknown>;
  return (
    getIndividualField(ind, ["practiceName", "practice_name"]) ||
    getIndividualField(ind, ["practice", "firm", "lawFirm"]) ||
    "-"
  );
};

const isLawFirmAdminOrOwnerRole = (role?: string | null): boolean => {
  const normalized = String(role || "")
    .trim()
    .toLowerCase();
  return normalized === "owner" || normalized === "admin";
};

const getVisa = (matter: MatterDTO): string => {
  const fromCase = asString(matter.case?.visa);
  if (fromCase) return fromCase;
  const ind = (matter.individual ?? {}) as Record<string, unknown>;
  return asString(ind.visa);
};

const formatShortDateLabel = (value?: string | null): string => {
  const raw = (value ?? "").trim();
  if (!raw) return "—";
  const formatted = dayjs(raw).format("MM/DD/YYYY");
  return formatted && formatted !== "Invalid Date" ? formatted : "—";
};

const toCaseWorkspacePath = (matter: MatterDTO): string =>
  caseWorkspacePath(matterCaseRef(matter));

const Muted = ({ children }: { children: React.ReactNode }) => (
  <span className="whitespace-nowrap text-fg-3">{children}</span>
);

const isRfeMatter = (matter: MatterDTO): boolean => {
  const ind = matter.individual as unknown as {
    is_rfe?: unknown;
    isRfe?: unknown;
  } | null;
  return Boolean(ind?.is_rfe ?? ind?.isRfe);
};

const workflowTypeOf = (matter: MatterDTO): string | null | undefined =>
  (matter.individual as unknown as { workflow_type?: string | null } | null)
    ?.workflow_type;

const CasesV2Inner = () => {
  const TABLE_INFINITE_SCROLL_THRESHOLD_PX = 200;
  useAppHeader(CASES_HEADER);
  const navigate = useNavigate();
  const { isSuperAdmin, lawyerId, lawyerPractice, role } =
    useUserRoleAndSubscription();
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const infiniteScrollInFlightRef = useRef(false);
  const {
    filteredItems,
    items,
    isLoading,
    error,
    statusFilter,
    visaFilter,
    attorneyFilter,
    searchInput,
    sortBy,
    sortDir,
    setStatusFilter,
    setVisaFilter,
    setAttorneyFilter,
    setSearch,
    setSort,
    refresh,
    canGoNext,
    loadMore,
    isLoadingMore,
    totalCount
  } = useMattersDashboard();

  const { ensure: ensureUserProfiles, get: getUserProfile } = useUserProfiles();

  const { options: practiceTeamMemberOptions } =
    usePracticeTeamMembers(lawyerPractice);

  useEffect(() => {
    const ids = new Set<string>();
    filteredItems.forEach((m) => {
      (m.assigned_to_user_ids ?? []).forEach((id) => {
        const trimmed = (id ?? "").trim();
        if (trimmed) ids.add(trimmed);
      });
      const foreignNationalId = getForeignNationalId(m);
      if (foreignNationalId) ids.add(foreignNationalId);
    });
    practiceTeamMemberOptions.forEach((member) => {
      const trimmed = (member.uid ?? "").trim();
      if (trimmed) ids.add(trimmed);
    });
    if (ids.size === 0) return;
    ensureUserProfiles(Array.from(ids));
  }, [ensureUserProfiles, filteredItems, practiceTeamMemberOptions]);

  const isUnfiltered =
    !statusFilter &&
    !visaFilter &&
    !attorneyFilter.trim() &&
    !searchInput.trim();
  const [chips, setChips] = useState<CaseChipCounts | null>(null);
  useEffect(() => {
    if (!isUnfiltered || isLoading || isLoadingMore || error) return;
    setChips(
      caseChipCounts(items, {
        total: totalCount,
        complete: !canGoNext,
        uid: lawyerId
      })
    );
  }, [
    canGoNext,
    error,
    isLoading,
    isLoadingMore,
    isUnfiltered,
    items,
    lawyerId,
    totalCount
  ]);

  useEffect(() => {
    if (!isLoadingMore) infiniteScrollInFlightRef.current = false;
  }, [isLoadingMore]);

  const showUpdatingOverlay = isLoading && items.length > 0;

  const maybeLoadMore = useCallback(() => {
    const el = tableScrollContainerRef.current;
    if (!el) return;
    if (error) return;
    if (!canGoNext) return;
    if (isLoadingMore) return;
    if (infiniteScrollInFlightRef.current) return;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceFromBottom > TABLE_INFINITE_SCROLL_THRESHOLD_PX) return;

    infiniteScrollInFlightRef.current = true;
    loadMore();
  }, [
    TABLE_INFINITE_SCROLL_THRESHOLD_PX,
    canGoNext,
    error,
    isLoadingMore,
    loadMore
  ]);

  useEffect(() => {
    if (isLoading) return;
    maybeLoadMore();
  }, [isLoading, items.length, maybeLoadMore]);

  const showTeamColumn = isSuperAdmin || isLawFirmAdminOrOwnerRole(role);
  const teamColumnHeader = isSuperAdmin ? "Law Firm" : "Team";

  const columns = useMemo<ColumnDef<MatterDTO, unknown>[]>(() => {
    const cols: ColumnDef<MatterDTO, unknown>[] = [
      {
        id: "client",
        header: "Case",
        meta: { className: "w-full max-w-0 min-w-48" },
        cell: ({ row }) => {
          const matter = row.original;
          const clientName = getClientName(matter);
          const fnId = getForeignNationalId(matter);
          const avatar = fnId
            ? getUserProfile(fnId)?.avatar_data_url ?? null
            : null;
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar size="md" shape="square">
                {avatar ? <AvatarImage src={avatar} alt="" /> : null}
                <AvatarFallback>{initialsFromLabel(clientName)}</AvatarFallback>
              </Avatar>
              <span className="min-w-0 truncate whitespace-nowrap text-fg">
                {clientName}
              </span>
            </div>
          );
        }
      },
      {
        id: "visa",
        header: "Visa Type",
        cell: ({ row }) => {
          const visa = getVisa(row.original);
          return visa ? (
            <VisaTag
              visaType={asVisaValue(visa)}
              size="sm"
              isRfe={isRfeMatter(row.original)}
              workflowType={workflowTypeOf(row.original)}
              label={caseTypeLabel(row.original.case)}
            />
          ) : (
            <Muted>—</Muted>
          );
        }
      },
      {
        id: "deadline",
        header: "Filing Deadline",
        cell: ({ row }) => (
          <Muted>
            {formatShortDateLabel(asString(row.original.case?.filing_deadline))}
          </Muted>
        )
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <CaseStatusPill
            status={row.original.case?.status ?? CaseStatus.IN_PROGRESS}
          />
        )
      },
      {
        id: "petitioner",
        header: "Petitioner",
        cell: ({ row }) => {
          const name = getPetitionerName(row.original);
          return (
            <span
              className="block max-w-40 truncate text-fg-3"
              title={name === "-" ? "" : name}
            >
              {name}
            </span>
          );
        }
      },
      {
        id: "assignment",
        header: "Assignment",
        cell: ({ row }) => {
          const ids = row.original.assigned_to_user_ids ?? [];
          const profiles = row.original.assigned_to_user_profiles ?? [];
          return ids.length || profiles.length ? (
            <AssignedUsersAvatarStack userProfiles={profiles} userIds={ids} />
          ) : (
            <Muted>—</Muted>
          );
        }
      }
    ];
    if (showTeamColumn) {
      cols.push({
        id: "team",
        header: teamColumnHeader,
        cell: ({ row }) => (
          <span
            className="block max-w-32 truncate text-fg-3"
            title={getPracticeName(row.original)}
          >
            {getPracticeName(row.original)}
          </span>
        )
      });
    }
    cols.push({
      id: "created",
      header: "Date Created",
      cell: ({ row }) => (
        <Muted>
          {formatShortDateLabel(asString(row.original.case?.created_at))}
        </Muted>
      )
    });
    return cols;
  }, [getUserProfile, showTeamColumn, teamColumnHeader]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-surface">
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Cases"
          count={isLoading && totalCount === 0 ? null : totalCount}
          description={casesDescription({
            isSuperAdmin,
            practiceName: lawyerPractice
          })}
          actions={
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(NEW_CASE_PATH)}
            >
              <Plus />
              New Case
            </Button>
          }
        />
        <CasesTableToolbar
          searchInput={searchInput}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          visaFilter={visaFilter}
          onVisaChange={setVisaFilter}
          attorneyFilter={attorneyFilter}
          onAttorneyChange={setAttorneyFilter}
          practiceTeamMemberOptions={practiceTeamMemberOptions}
          currentUserId={lawyerId}
          chips={chips}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={setSort}
          onRefresh={refresh}
        />
      </div>

      <div
        ref={tableScrollContainerRef}
        className="relative flex min-h-0 w-full flex-1 flex-col overflow-auto"
        onScroll={maybeLoadMore}
      >
        {error ? (
          <EmptyState
            icon={TriangleAlert}
            title="Couldn't Load Cases"
            description="Something went wrong loading the cases table."
            action={
              <Button variant="secondary" size="sm" onClick={refresh}>
                Retry
              </Button>
            }
            className="flex-1"
          />
        ) : (
          <>
            <DataTable
              data={filteredItems}
              columns={columns}
              rowHeight="double"
              getRowId={(m) => `${m.case_id}-${m.individual_id}`}
              onRowClick={(m) => navigate(toCaseWorkspacePath(m))}
              isLoading={isLoading && items.length === 0}
              className="overflow-visible"
              empty={
                <EmptyState
                  icon={Briefcase}
                  title="No Matching Cases"
                  description="Try a different search or clear the filters."
                  className="py-16"
                />
              }
            />

            {showUpdatingOverlay ? (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-surface/60">
                <Spinner className="size-5 text-fg-3" />
              </div>
            ) : null}

            {isLoadingMore ? (
              <div className="flex w-full justify-center py-4">
                <Spinner className="size-4 text-fg-3" />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export const CasesV2 = () => {
  return (
    <MattersDashboardProvider initialLimit={50}>
      <CasesV2Inner />
    </MattersDashboardProvider>
  );
};
